import client from '../client';
import type { AbandonedCart } from '../../types/cart';
import type { ApiResponse, PaginationParams } from '../../types/api';

interface CartListParams extends PaginationParams {
  startDate?: string;
  endDate?: string;
}

// Why a customer drops out of a bulk run. `count` is the reachable remainder:
// contactable, not opted out, and not contacted inside `cooldownHours`.
export interface RetargetCount {
  count: number;
  onCooldown: number;
  unreachable: number;
  unsubscribed: number;
  available: boolean;
  cooldownHours?: number;
}

// Sent -> opened -> clicked -> converted for this store's retargeting messages.
// Open rate is inflated by mailbox providers that prefetch images, so click
// rate is the more trustworthy engagement signal.
export interface RetargetingFunnel {
  sent: number;
  opened: number;
  clicked: number;
  converted: number;
  revenue: number;
  openRate: number;
  clickRate: number;
  conversionRate: number;
  attributionDays: number;
}

export interface CartStats {
  totalCarts: number;
  totalValue: number;
  recoveredCount: number;
  recoveryRate: number;
  retargeting?: RetargetingFunnel;
}

export interface RetargetResult {
  queued: number;
  skipped: number;
  onCooldown: number;
  unreachable: number;
  unsubscribed: number;
  cooldownHours?: number;
}

export const cartsApi = {
  list: async (storeId: string, params?: CartListParams) => {
    const res = await client.get<ApiResponse<{ carts: AbandonedCart[]; pagination: { page: number; limit: number; total: number; pages: number }; stats?: CartStats }>>(
      `/dashboard/${storeId}/carts`,
      { params },
    );
    return res.data.data!;
  },

  getRetargetCount: async (storeId: string, platform: string, filters?: { minCartValue?: number; maxCartValue?: number }) => {
    const res = await client.post<ApiResponse<RetargetCount>>(
      `/dashboard/${storeId}/carts/retarget/count`,
      { platform, filters },
    );
    return res.data.data!;
  },

  // Queues a background run: `queued` is how many customers were claimed for
  // sending, not how many have already been delivered.
  bulkRetarget: async (storeId: string, data: { platform: string; message: string; filters?: { minCartValue?: number; maxCartValue?: number } }) => {
    const res = await client.post<ApiResponse<RetargetResult>>(
      `/dashboard/${storeId}/carts/retarget`,
      data,
    );
    return res.data.data!;
  },

  sendRecovery: async (storeId: string, cartId: string, data: {
    channel: 'email' | 'whatsapp';
    message?: string;
    template?: string;
    subject?: string;
    colors?: Record<string, string>;
    greeting?: string;
    ctaText?: string;
    ctaUrl?: string;
  }) => {
    await client.post(`/dashboard/${storeId}/carts/${cartId}/recover`, data);
  },
};
